import { isCollectionBlock } from '@/lib/blocks/collection/blocks';
import { BLOCK_STACK_DIRECTION_DEFAULT } from '@/lib/blocks/layout/block-stack-layout';
import { type GroupDirection, resolveGroupLayout } from '@/lib/blocks/layout/group-layout';
import { buildBlockTree, COLUMN_SPAN_DEFAULT, clampColumnSpan } from '@/lib/blocks/tree';
import type { PageBlock } from '@/types/block';

type ParentDirection = GroupDirection | 'grid' | null;

const CONTENT_GROUP_MIN_COLUMN_SPAN = 2;
const shouldBalanceHorizontalSiblingSpans = (
  siblings: PageBlock[],
  updates: Map<string, PageBlock>,
) => {
  const normalizedSpans = siblings.map((child) =>
    clampColumnSpan((updates.get(child.id) || child).columnSpan),
  );
  const totalSpan = normalizedSpans.reduce((sum, span) => sum + span, 0);

  if (totalSpan > COLUMN_SPAN_DEFAULT) {
    return true;
  }

  if (totalSpan === COLUMN_SPAN_DEFAULT) {
    return false;
  }

  const [firstSpan] = normalizedSpans;
  return firstSpan !== undefined && normalizedSpans.every((span) => span === firstSpan);
};

function normalizeSiblingSpans(
  siblings: PageBlock[],
  direction: ParentDirection,
  updates: Map<string, PageBlock>,
) {
  if (siblings.length === 0 || direction === null) {
    return;
  }

  if (direction === 'grid' || direction === 'vertical') {
    siblings.forEach((child) => {
      const normalizedChild = updates.get(child.id) || child;
      if (normalizedChild.columnSpan === COLUMN_SPAN_DEFAULT) {
        return;
      }

      updates.set(child.id, { ...normalizedChild, columnSpan: COLUMN_SPAN_DEFAULT });
    });
    return;
  }

  if (!shouldBalanceHorizontalSiblingSpans(siblings, updates)) {
    return;
  }

  const childCount = siblings.length;
  const effectiveCount = Math.min(
    childCount,
    Math.floor(COLUMN_SPAN_DEFAULT / CONTENT_GROUP_MIN_COLUMN_SPAN),
  );
  const baseSpan = Math.floor(COLUMN_SPAN_DEFAULT / effectiveCount);
  const remainder = COLUMN_SPAN_DEFAULT % effectiveCount;

  siblings.forEach((child, index) => {
    const normalizedChild = updates.get(child.id) || child;
    const balancedSpan = index < remainder ? baseSpan + 1 : baseSpan;
    const nextColumnSpan = clampColumnSpan(balancedSpan);

    if (normalizedChild.columnSpan === nextColumnSpan) {
      return;
    }

    updates.set(child.id, {
      ...normalizedChild,
      columnSpan: nextColumnSpan,
    });
  });
}

export function normalizeBlocksStructure(
  blocks: PageBlock[],
  rootParentDirection: GroupDirection = BLOCK_STACK_DIRECTION_DEFAULT,
): PageBlock[] {
  // Shared structural normalization for any edit that changes tree shape.
  // Delete, drag/drop, and similar operations should converge here so sibling
  // ordering and column spans are recalculated the same way.
  const { topLevel, childrenByParent } = buildBlockTree(blocks);
  const blockMap = new Map(blocks.map((block) => [block.id, block]));
  const updates = new Map<string, PageBlock>();

  const normalizeSiblings = (siblings: PageBlock[], parentId: string | null) => {
    siblings.forEach((block, index) => {
      const nextParentBlockId = parentId;
      const nextPosition = index + 1;
      const nextColumnSpan = clampColumnSpan(block.columnSpan);

      if (
        (block.parentBlockId ?? null) === nextParentBlockId &&
        block.position === nextPosition &&
        block.columnSpan === nextColumnSpan
      ) {
        return;
      }

      updates.set(block.id, {
        ...block,
        parentBlockId: nextParentBlockId,
        position: nextPosition,
        columnSpan: nextColumnSpan,
      });
    });
  };

  normalizeSiblings(topLevel, null);
  childrenByParent.forEach((siblings, parentId) => {
    normalizeSiblings(siblings, parentId);
  });

  // Top-level blocks in a vertical section use columnSpan as a max-width constraint,
  // not a grid layout span. Normalizing them would reset user-configured widths.
  if (rootParentDirection !== 'vertical') {
    normalizeSiblingSpans(topLevel, rootParentDirection, updates);
  }

  childrenByParent.forEach((siblings, parentId) => {
    const parentBlock = blockMap.get(parentId);
    if (!parentBlock) {
      return;
    }

    if (isCollectionBlock(parentBlock)) {
      normalizeSiblingSpans(siblings, 'grid', updates);
      return;
    }

    if (parentBlock.blockType === 'group') {
      const layout = resolveGroupLayout(parentBlock, siblings);
      // Float-mode groups use each child's columnSpan directly as its float width (see
      // getFloatChildClassName) — rebalancing spans to sum to 12 would fight that, overwriting
      // widths a site import preserved from the original layout.
      if (!layout.isFloatLayout) {
        normalizeSiblingSpans(siblings, layout.direction, updates);
      }
    }
  });

  if (updates.size === 0) {
    return blocks;
  }

  return blocks.map((block) => updates.get(block.id) || block);
}
